import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '../services/supabaseClient';
import { useAuth } from '../context/AuthContext';
import { useAppData } from '../context/AppDataContext';
import { useZones } from './useZones';

const toCleanMac = (mac) => (mac ? mac.replace(/[^0-9A-Fa-f]/g, '').toUpperCase() : '');

function parsePayloadLocation(payload) {
  if (!payload) return null;
  const data = typeof payload === 'string' ? JSON.parse(payload) : payload;
  const loc = data?.location || data?.loc;
  if (!loc) return null;
  const latitude = loc.latitude ?? loc.lat;
  const longitude = loc.longitude ?? loc.lon ?? loc.lng;
  if (latitude == null || longitude == null) return null;
  return {
    latitude: Number(latitude),
    longitude: Number(longitude),
    altitude: loc.altitude ?? loc.horizontal ?? null,
    recordedAt: data?.time ? new Date(Number(data.time) * 1000).toISOString() : null,
  };
}

function isRecent(iso, minutes = 30) {
  if (!iso) return false;
  const age = Date.now() - new Date(iso).getTime();
  return age >= 0 && age <= minutes * 60 * 1000;
}

export function useFleetMap({ from = null, to = null } = {}) {
  const { user, isAuthenticated } = useAuth();
  const { sensors, gateways } = useAppData();
  const { zones, isLoading: zonesLoading } = useZones();
  const [liveSensorPositions, setLiveSensorPositions] = useState({});
  const [liveGatewayPositions, setLiveGatewayPositions] = useState({});
  const [loadingLive, setLoadingLive] = useState(true);
  const gatewayMacsRef = useRef(new Set());
  const hasTimeFilter = Boolean(from && to);

  const loadLatestPositions = useCallback(async () => {
    if (!user?.id) {
      setLiveSensorPositions({});
      setLiveGatewayPositions({});
      setLoadingLive(false);
      return;
    }

    setLoadingLive(true);
    try {
      const { data: trackRows, error: trackError } = await supabase
        .from('gps_tracks')
        .select('sensor_id, zone_id, latitude, longitude, speed, heading, temperature, humidity, battery_level, recorded_at')
        .eq('user_id', user.id)
        .gte('recorded_at', from || '1970-01-01T00:00:00.000Z')
        .lte('recorded_at', to || new Date().toISOString())
        .order('recorded_at', { ascending: false })
        .limit(1000);

      if (trackError) throw trackError;

      const sensorLatest = {};
      (trackRows || []).forEach((row) => {
        if (!row.sensor_id || sensorLatest[row.sensor_id]) return;
        if (row.latitude == null || row.longitude == null) return;
        sensorLatest[row.sensor_id] = {
          latitude: Number(row.latitude),
          longitude: Number(row.longitude),
          recordedAt: row.recorded_at,
          speed: row.speed,
          heading: row.heading,
          temperature: row.temperature != null ? Number(row.temperature) : null,
          humidity: row.humidity != null ? Number(row.humidity) : null,
          batteryLevel: row.battery_level != null ? Number(row.battery_level) : null,
          zoneId: row.zone_id,
          source: 'gps_tracks',
          isLive: isRecent(row.recorded_at, 15),
        };
      });
      setLiveSensorPositions(sensorLatest);

      const macs = (gateways || [])
        .map((g) => toCleanMac(g.gateway_mac_hex || g.mac_address))
        .filter(Boolean);
      gatewayMacsRef.current = new Set(macs);

      if (macs.length === 0) {
        setLiveGatewayPositions({});
        return;
      }

      const gatewayLatest = {};
      await Promise.all(
        macs.map(async (mac) => {
          const { data: rows } = await supabase
            .from('user_raw_gateway_data')
            .select('payload, received_at, gateway_mac')
            .eq('gateway_mac', mac)
            .gte('received_at', from || '1970-01-01T00:00:00.000Z')
            .lte('received_at', to || new Date().toISOString())
            .order('received_at', { ascending: false })
            .limit(5);

          for (const row of rows || []) {
            const loc = parsePayloadLocation(row.payload);
            if (!loc) continue;
            const gateway = (gateways || []).find(
              (g) => toCleanMac(g.gateway_mac_hex || g.mac_address) === mac
            );
            if (!gateway) continue;
            gatewayLatest[gateway.id] = {
              ...loc,
              recordedAt: row.received_at || loc.recordedAt,
              gatewayMac: mac,
              source: 'gateway_mqtt',
              isLive: isRecent(row.received_at, 15),
            };
            break;
          }
        })
      );
      setLiveGatewayPositions(gatewayLatest);
    } catch (err) {
      console.error('useFleetMap: failed to load positions', err);
    } finally {
      setLoadingLive(false);
    }
  }, [user?.id, gateways, from, to]);

  useEffect(() => {
    loadLatestPositions();
  }, [loadLatestPositions]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id || hasTimeFilter) return undefined;

    const channel = supabase
      .channel(`fleet-map-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'gps_tracks',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const row = payload.new;
          if (!row?.sensor_id || row.latitude == null || row.longitude == null) return;
          setLiveSensorPositions((prev) => ({
            ...prev,
            [row.sensor_id]: {
              latitude: Number(row.latitude),
              longitude: Number(row.longitude),
              recordedAt: row.recorded_at,
              speed: row.speed,
              heading: row.heading,
              temperature: row.temperature != null ? Number(row.temperature) : null,
              humidity: row.humidity != null ? Number(row.humidity) : null,
              batteryLevel: row.battery_level != null ? Number(row.battery_level) : null,
              zoneId: row.zone_id,
              source: 'gps_tracks',
              isLive: true,
            },
          }));
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_raw_gateway_data',
        },
        (payload) => {
          const row = payload.new;
          const mac = toCleanMac(row?.gateway_mac);
          if (!mac || !gatewayMacsRef.current.has(mac)) return;
          const loc = parsePayloadLocation(row.payload);
          if (!loc) return;
          const gateway = (gateways || []).find(
            (g) => toCleanMac(g.gateway_mac_hex || g.mac_address) === mac
          );
          if (!gateway) return;
          setLiveGatewayPositions((prev) => ({
            ...prev,
            [gateway.id]: {
              ...loc,
              recordedAt: row.received_at || loc.recordedAt,
              gatewayMac: mac,
              source: 'gateway_mqtt',
              isLive: true,
            },
          }));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isAuthenticated, user?.id, gateways, hasTimeFilter]);

  const zoneById = useMemo(() => {
    const map = {};
    (zones || []).forEach((z) => {
      map[z.id] = z;
    });
    return map;
  }, [zones]);

  const mapMarkers = useMemo(() => {
    const items = [];

    (zones || []).forEach((zone) => {
      if (zone.latitude == null || zone.longitude == null) return;
      items.push({
        id: `zone-${zone.id}`,
        type: 'zone',
        name: zone.name,
        lat: Number(zone.latitude),
        lng: Number(zone.longitude),
        isLive: false,
        meta: zone,
      });
    });

    (sensors || []).forEach((sensor) => {
      const live = liveSensorPositions[sensor.id];
      // A historical query should only show sensors with telemetry in that range.
      if (hasTimeFilter && !live) return;
      const zone = sensor.zone_id ? zoneById[sensor.zone_id] : null;
      const lat = live?.latitude ?? sensor.latitude ?? zone?.latitude;
      const lng = live?.longitude ?? sensor.longitude ?? zone?.longitude;
      if (lat == null || lng == null) return;

      const latest = sensor.latest_reading;
      const tripHasTemp = live?.temperature != null;
      const tripHasHumidity = live?.humidity != null;
      const temperature = tripHasTemp ? live.temperature : (latest?.temperature ?? null);
      const humidity = tripHasHumidity ? live.humidity : (latest?.humidity ?? null);
      const readingAt = tripHasTemp || tripHasHumidity
        ? live?.recordedAt
        : (latest?.recorded_at ?? null);

      items.push({
        id: `sensor-${sensor.id}`,
        type: 'sensor',
        name: sensor.display_name || sensor.name || `Sensor ${String(sensor.id).slice(0, 6)}`,
        lat: Number(lat),
        lng: Number(lng),
        isLive: hasTimeFilter ? false : !!live?.isLive,
        recordedAt: live?.recordedAt,
        temperature,
        humidity,
        temperatureUnit: sensor.temperature_unit || 'celsius',
        readingAt,
        readingSource: tripHasTemp || tripHasHumidity ? 'trip' : (latest ? 'sensor' : null),
        source: live ? 'phone_gps' : 'zone_static',
        meta: sensor,
      });
    });

    (gateways || []).forEach((gateway) => {
      const live = liveGatewayPositions[gateway.id];
      if (!live) return;
      items.push({
        id: `gateway-${gateway.id}`,
        type: 'gateway',
        name: gateway.name || gateway.mac_address || `Gateway ${String(gateway.id).slice(0, 6)}`,
        lat: live.latitude,
        lng: live.longitude,
        isLive: live.isLive,
        recordedAt: live.recordedAt,
        source: 'gateway_gps',
        meta: gateway,
      });
    });

    return items;
  }, [zones, sensors, gateways, liveSensorPositions, liveGatewayPositions, zoneById, hasTimeFilter]);

  const mapCenter = useMemo(() => {
    if (mapMarkers.length === 0) return [-33.8688, 151.2093];
    const latSum = mapMarkers.reduce((s, m) => s + m.lat, 0);
    const lngSum = mapMarkers.reduce((s, m) => s + m.lng, 0);
    return [latSum / mapMarkers.length, lngSum / mapMarkers.length];
  }, [mapMarkers]);

  return {
    mapMarkers,
    mapCenter,
    zones,
    sensors,
    gateways,
    isLoading: zonesLoading || loadingLive,
    hasTimeFilter,
    refresh: loadLatestPositions,
    counts: {
      zones: mapMarkers.filter((m) => m.type === 'zone').length,
      sensors: mapMarkers.filter((m) => m.type === 'sensor').length,
      gateways: mapMarkers.filter((m) => m.type === 'gateway').length,
      live: mapMarkers.filter((m) => m.isLive).length,
    },
  };
}
